import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Avatar,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  useTheme,
  alpha,
  Skeleton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import LogoutIcon from '@mui/icons-material/Logout';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BottomNav from './BottomNav';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { format } from 'date-fns';

interface Appointment {
  id: string;
  customerName: string;
  customerPhone: string;
  appointmentDate: string;
  appointmentTime: string;
  status: string;
  service?: string;
}

const ModernDashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const [todayAppointments, setTodayAppointments] = useState<Appointment[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        try {
          const userDoc = await db.collection('users').doc(currentUser.uid).get();
          setUserData({
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName || 'Staff Member',
            ...userDoc.data()
          });
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };

    const fetchTodayAppointments = async () => {
      try {
        setLoadingAppointments(true);
        const today = new Date();
        const formattedDate = format(today, 'yyyy-MM-dd');

        const q = query(
          collection(db, 'appointments'),
          where('appointmentDate', '==', formattedDate),
          orderBy('appointmentTime', 'asc')
        );

        const querySnapshot = await getDocs(q);
        const appointments: Appointment[] = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data() as Omit<Appointment, 'id'>
        }));

        setTodayAppointments(appointments);
      } catch (error) {
        console.error('Error fetching appointments:', error);
      } finally {
        setLoadingAppointments(false);
      }
    };

    fetchUserData();
    fetchTodayAppointments();
  }, []);

  const handleLogout = async () => {
    try {
      setLoading(true);
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAvatarLetter = () => {
    return userData?.displayName?.charAt(0)?.toUpperCase() || 'S';
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const quickActions = [
    {
      title: 'Schedule',
      icon: EventNoteIcon,
      path: '/appointments',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#667eea'
    },
    {
      title: 'Earnings',
      icon: AttachMoneyIcon,
      path: '/commissions',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      color: '#f093fb'
    },
    {
      title: 'Documents',
      icon: DescriptionIcon,
      path: '/documents',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      color: '#4facfe'
    },
    {
      title: 'Analytics',
      icon: BarChartIcon,
      path: '/analytics',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      color: '#43e97b'
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 10 }}>
      {/* Modern Header with Gradient */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '0 0 32px 32px',
          pt: 3,
          pb: 4,
          px: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v6h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3,
          },
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: alpha('#ffffff', 0.2),
                  backdropFilter: 'blur(10px)',
                  border: '3px solid rgba(255, 255, 255, 0.3)',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#ffffff',
                }}
              >
                {getAvatarLetter()}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                  Welcome back! 👋
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  {userData?.displayName || 'Staff Member'}
                </Typography>
              </Box>
            </Box>

            <IconButton
              onClick={handleLogout}
              disabled={loading}
              sx={{
                bgcolor: alpha('#ffffff', 0.2),
                color: 'white',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  bgcolor: alpha('#ffffff', 0.3),
                },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : <LogoutIcon />}
            </IconButton>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ mt: -2, position: 'relative', zIndex: 2 }}>
        {/* Today's Stats Card */}
        <Card
          sx={{
            mb: 3,
            background: 'white',
            borderRadius: '24px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
              p: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AccessTimeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                Today's Schedule
              </Typography>
            </Box>

            {loadingAppointments ? (
              <Box>
                {[1, 2].map((i) => (
                  <Box key={i} sx={{ mb: 1.5 }}>
                    <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '12px' }} />
                  </Box>
                ))}
              </Box>
            ) : todayAppointments.length > 0 ? (
              <Box>
                {todayAppointments.slice(0, 3).map((apt, index) => (
                  <Box
                    key={apt.id}
                    sx={{
                      p: 1.5,
                      mb: 1,
                      borderRadius: '12px',
                      bgcolor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: index % 2 === 0
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                        {apt.appointmentTime.split(':')[0]}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', mt: -0.5 }}>
                        {apt.appointmentTime.split(':')[1]}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          mb: 0.25,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {apt.customerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {apt.service || 'Appointment'}
                      </Typography>
                    </Box>

                    <Chip
                      label="Upcoming"
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />
                  </Box>
                ))}

                {todayAppointments.length > 3 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'block',
                      textAlign: 'center',
                      mt: 1,
                    }}
                    onClick={() => handleNavigate('/appointments')}
                  >
                    View all {todayAppointments.length} appointments →
                  </Typography>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'white',
                  borderRadius: '12px',
                }}
              >
                <EventNoteIcon sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No appointments scheduled
                </Typography>
              </Box>
            )}
          </Box>
        </Card>

        {/* Quick Actions */}
        <Typography variant="h6" sx={{ mb: 2, px: 1, fontWeight: 700 }}>
          Quick Actions
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <Card
                key={action.path}
                onClick={() => handleNavigate(action.path)}
                sx={{
                  cursor: 'pointer',
                  overflow: 'hidden',
                  position: 'relative',
                  '&:active': {
                    transform: 'scale(0.98)',
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '16px',
                      background: action.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1.5,
                      boxShadow: `0 8px 16px ${alpha(action.color, 0.3)}`,
                    }}
                  >
                    <Icon sx={{ fontSize: 28, color: 'white' }} />
                  </Box>

                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {action.title}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TrendingUpIcon sx={{ fontSize: 14, color: theme.palette.success.main }} />
                    <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 600 }}>
                      Active
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Container>

      {/* Bottom Navigation */}
      <BottomNav />
    </Box>
  );
};

export default ModernDashboard;
